import { FC, useState } from 'react';
import { Crown } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { isTrialExpired, isAccountExpired, getRemainingGraceDays } from '@/utils/subscription';
import { useStripePayment } from '@/hooks/useStripePayment';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BookOpen } from 'lucide-react';
import { MessageCircle } from 'lucide-react';
import { Trophy } from 'lucide-react';

export const PremiumTrialExpiredBanner: FC = () => {
  const { userProfile } = useProfile();
  const { handlePayment, isLoading } = useStripePayment();
  const { user } = useAuth();
  const [showBenefits, setShowBenefits] = useState(false);

  // Si no hay perfil, no mostramos el banner
  if (!userProfile) return null;

  // Si el usuario es premium o es el administrador, no mostramos el banner
  if (userProfile.subscription_status === 'active' || 
      userProfile.email === 'welingtondesosa@gmail.com' || 
      userProfile.role === 'admin') {
    return null;
  }

  // Verificamos si el trial ha expirado
  const trialExpired = isTrialExpired(userProfile.trial_start_date);
  
  // Si el trial no ha expirado, no mostramos el banner
  if (!trialExpired) return null;

  // Verificamos si la cuenta ha expirado completamente (después del período de gracia)
  const accountExpired = isAccountExpired(userProfile.trial_start_date);
  
  // Si la cuenta ha expirado completamente, no mostramos el banner
  if (accountExpired) return null;

  // Calculamos los días restantes del período de gracia
  const daysRemaining = getRemainingGraceDays(userProfile.trial_start_date);

  const handleUpgrade = async () => {
    if (!user?.id) {
      toast.error('Debes iniciar sesión para continuar');
      return;
    }

    try {
      await handlePayment(user.id);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Error al procesar el pago');
      }
    }
  };

  return (
    <>
      <div className="w-full bg-gradient-to-r from-primary via-secondary to-accent p-4 text-white">
        <div className="container mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Crown className="h-5 w-5 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold">¡No pierdas tu progreso! {daysRemaining} días restantes</h3>
                <p className="text-sm opacity-90">
                  Tu periodo de prueba ha terminado. Actualiza a Premium antes de que se elimine tu cuenta
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => setShowBenefits(true)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-md text-sm font-medium transition-colors w-full sm:w-auto"
              >
                Ver beneficios
              </button>
              <button
                onClick={handleUpgrade}
                disabled={isLoading}
                className="px-4 py-2 bg-white hover:bg-white/90 text-primary rounded-md text-sm font-medium transition-colors w-full sm:w-auto disabled:opacity-50"
              >
                {isLoading ? 'Procesando...' : 'Activar Premium'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showBenefits} onOpenChange={setShowBenefits}>
        <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Beneficios Premium
            </DialogTitle>
            <DialogDescription className="text-center mt-2 text-sm">
              Desbloquea todo el potencial de Financieramente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-lg border border-purple-100">
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <Crown className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-purple-900">Acceso Ilimitado</h4>
                <p className="text-sm text-purple-700 mt-1">
                  Todas las lecciones y juegos sin restricciones.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <BookOpen className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-blue-900">Contenido Exclusivo</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Recursos especializados y materiales descargables.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg border border-emerald-100">
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <MessageCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-emerald-900">Soporte Prioritario</h4>
                <p className="text-sm text-emerald-700 mt-1">
                  Atención personalizada y respuesta rápida.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg border border-amber-100">
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <Trophy className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-amber-900">Programa de Socios</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Acceso exclusivo al programa de socios después de 3 meses.
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                setShowBenefits(false);
                handleUpgrade();
              }}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-primary via-secondary to-accent text-white py-3 rounded-lg font-semibold text-base hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isLoading ? 'Procesando...' : '¡Activar Premium Ahora!'}
            </button>

            <p className="text-center text-xs text-muted-foreground">
              Únete a más de 1,000 estudiantes que ya están transformando su futuro financiero
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
